import type { WorkflowRunPayload } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import WorkflowRunDocumentStageView from '@UI/Store/WorkflowRuns/Views/WorkflowRunDocumentStageView';
import WorkflowRunDocumentView from '@UI/Store/WorkflowRuns/Views/WorkflowRunDocumentView';
import WorkflowRunMissingSourceView from '@UI/Store/WorkflowRuns/Views/WorkflowRunMissingSourceView';
import WorkflowRunSourceView from '@UI/Store/WorkflowRuns/Views/WorkflowRunSourceView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * A run in full. `state` is `open`, `in_progress` or `completed`. `canGenerate`, `blockedReason` and
 * `canAbandon`, `isGenerating` and `canMarkHandled` are copied from the server, never recomputed. The facility is not carried: it comes from
 * the route.
 */
class WorkflowRunView {
	readonly id: number;
	readonly beneficiaryId: number | null;
	readonly workflowKey: string;
	readonly version: number;
	readonly label: string;
	readonly kind: LabeledValueView;
	readonly state: LabeledValueView;
	readonly openedAt: Date;
	/** Meaningless while the run is open: read `launchedAt` instead. */
	readonly startedAt: Date;
	readonly completedAt: Date | null;
	readonly isGenerating: boolean;
	readonly handledAt: Date | null;
	readonly canMarkHandled: boolean;
	readonly canGenerate: boolean;
	readonly blockedReason: string | null;
	readonly canAbandon: boolean;
	readonly acceptsInterviews: boolean;
	readonly acceptsNotes: boolean;
	readonly acceptsDocuments: boolean;
	readonly sources: readonly WorkflowRunSourceView[];
	readonly missingAutomaticSources: readonly WorkflowRunMissingSourceView[];
	readonly documents: readonly WorkflowRunDocumentView[];

	constructor(payload: WorkflowRunPayload) {
		this.id = payload.id;
		this.beneficiaryId = payload.beneficiary?.id ?? null;
		this.workflowKey = payload.workflowKey;
		this.version = payload.version;
		this.label = payload.label;
		this.kind = new LabeledValueView(payload.kind);
		this.state = new LabeledValueView(payload.state);
		this.openedAt = new Date(payload.openedAt);
		this.startedAt = new Date(payload.startedAt);
		this.completedAt = payload.completedAt !== null ? new Date(payload.completedAt) : null;
		this.isGenerating = payload.isGenerating;
		this.handledAt = payload.handledAt !== null ? new Date(payload.handledAt) : null;
		this.canMarkHandled = payload.canMarkHandled;
		this.canGenerate = payload.canGenerate;
		this.blockedReason = payload.blockedReason;
		this.canAbandon = payload.canAbandon;
		this.acceptsInterviews = payload.acceptsInterviews;
		this.acceptsNotes = payload.acceptsNotes;
		this.acceptsDocuments = payload.acceptsDocuments;
		this.sources = payload.sources.map((source) => new WorkflowRunSourceView(source));
		this.missingAutomaticSources = payload.missingAutomaticSources.map(
			(source) => new WorkflowRunMissingSourceView(source),
		);
		this.documents = payload.documents.map((document) => new WorkflowRunDocumentView(document));

		Object.freeze(this);
	}

	/** Generation not launched yet: the run still receives sources. */
	get isOpen(): boolean {
		return this.state.value === 'open';
	}

	get isCompleted(): boolean {
		return this.state.value === 'completed';
	}

	/** At least one document waits for the reviewer. */
	get hasDocumentsToReview(): boolean {
		return this.documents.some((document) => document.isToReview);
	}

	/** When the generation was launched; `null` while the run is open. */
	get launchedAt(): Date | null {
		return this.isOpen ? null : this.startedAt;
	}

	/** Documents grouped by `stage`, stages in ascending order, documents in server order. */
	get documentStages(): readonly WorkflowRunDocumentStageView[] {
		const stageNumbers = [...new Set(this.documents.map((document) => document.stage))].sort(
			(first, second) => first - second,
		);

		return stageNumbers.map(
			(stageNumber) =>
				new WorkflowRunDocumentStageView(
					stageNumber,
					this.documents.filter((document) => document.stage === stageNumber),
				),
		);
	}
}

export default WorkflowRunView;
